import { use, useEffect, useRef } from 'react'

import config from '@app/config'
import { getContextEntity } from '@app/helpers'
import { trackConversationView } from '@app/services/tracking'

import { useLanguages, useConversationActorRole } from '.'

import { ChatContext } from '../context'
import { useConversation } from './conversation/useConversation'
import { useConversationLanguage } from './conversation/useConversationLanguage'

export const useCollectConversationMetrics = () => {
  const { state } = use(ChatContext)
  const conversation = useConversation()
  const conversationActorRole = useConversationActorRole()
  const { languages, ready: areLanguagesReady } = useLanguages()
  const lang = config.regionalSettings?.language
  const { isTranslationEnabled, conversationLanguage } =
    useConversationLanguage(conversation)
  const contextEntityId =
    conversation?.context?.listingId ??
    conversation?.context?.unitId ??
    conversation?.context?.projectId
  const trackedConversationIdRef = useRef<number | null>(null)

  useEffect(() => {
    const conversationId = conversation?.id

    if (!conversationId) {
      trackedConversationIdRef.current = null
      return
    }

    if (trackedConversationIdRef.current === conversationId) {
      return
    }

    const translatedFrom =
      languages.find((language) => language.id === conversationLanguage)?.id
    const isTranslationLanguagePending =
      !!conversationLanguage && !translatedFrom && !areLanguagesReady

    if (isTranslationLanguagePending || !conversation) {
      return
    }

    const listing = getContextEntity(state.listings, conversation)
    const isListingPending = contextEntityId != null && !listing

    if (isListingPending) {
      return
    }

    trackConversationView(
      conversationId,
      listing,
      {
        userLanguage: lang,
        translatedFrom,
        translated: isTranslationEnabled,
      },
      conversationActorRole
    )

    trackedConversationIdRef.current = conversationId
  }, [
    conversation?.id,
    contextEntityId,
    conversationActorRole,
    conversationLanguage,
    isTranslationEnabled,
    lang,
    languages,
    areLanguagesReady,
    state.listings,
  ])
}
